import { CollectionNameEnum, DocumentChangeInterface, OperationTypeEnum, User } from "@finway-group/shared/lib/models"
import * as Sentry from "@sentry/browser"
import { isNotification } from "Components/layout/partials/navbar/activity/templates/utils"
import { AuthService, LocalStorageService } from "Shared/services"
import store from "Shared/store"
import { fetchAllActivities } from "Shared/store/actions/activity/activityActions"
import { updateAnalytics } from "Shared/store/actions/analytics/analyticsActions"
import { delay } from "Shared/utils/helpers"
import io from "socket.io-client"
import {
    subscribeToBankConnection,
    subscribeToCompanySettings,
    subscribeToCostCenter,
    subscribeToCreditCards,
    subscribeToCurrencyExchange,
    subscribeToEmployee,
    subscribeToExpenseAccount,
    subscribeToCostCenter2,
    subscribeToExpenses,
    subscribeToPaymentSettings,
    subscribeToTransactions,
    subscribeToVendor,
    subscribeToWallet,
    subscribeToTeam,
    subscribeToTax,
    subscribeToCorporate,
    subscribeToExpenseTag,
    subscribeToFilter,
} from "."
import { subscribeToCompanyCallbacks } from "./companySettingsSocket.service"
import { subscribeToCostCenterCallbacks } from "./costCenterSocket.service"
import { subscribeToCreditCardCallbacks } from "./creditCardSocket.service"
import subscribeToCsvTemplate from "./csvTemplatesSocket.service"
import { subscribeToExpenseCallbacks } from "./expenseSocket.service"
import { isIncomingFilterEqualToLocal } from "./filterSocket.service"
import { subscribeToImportResult } from "./importResultSocket.service"
import { subscribeToInboxInvoiceCallbacks, subscribeToInboxInvoices } from "./inboxInvoiceSocket.service"
import { subscribeToTransactionCategory, subscribeToTransactionCategoryCallbacks } from "./transactionCategorySocket.service"
import { subscribeToTransactionsCallbacks } from "./transactionSocket.service"
import { subscribeToVendorCallbacks } from "./vendorSocket.service"
import { subscribeToWorkflow } from "./workflowSocket.service"

class SocketService {
    private socket: SocketIOClient.Socket
    private onSuccessInstance?: () => void
    private onErrorInstance?: (state: string) => void

    private loggedInUserId = ""
    private isReconnecting = false
    private location = ""

    private dispatch = store.dispatch

    constructor() {}

    public connect(loggedInUserId: string, onError?: (state: string) => void, onSuccess?: () => void) {
        this.loggedInUserId = loggedInUserId
        this.onErrorInstance = onError
        this.onSuccessInstance = onSuccess

        const self = this
        let connection: any
        if (!this.socket || !this.socket.connected) {
            connection = io.connect(process.env.REACT_APP_API_SYNC + "/dashboard", {
                query: `auth_token=${localStorage.accessToken}`,
                transports: ["websocket"],
                secure: true,
                timeout: 60000,
            })

            connection.on("reconnect_attempt", () => {
                connection.io.opts.transports = ["websocket", "polling"]
            })

            connection.on("connect_error", (err: any) => {
                onError && onError(err)
                console.warn("connect error", err)
                Sentry.captureException(`[Socket] connect error: ${typeof err === "string" ? err : JSON.stringify(err)}`)
            })

            connection.on("connect_failed", (err: any) => {
                onError && onError(err)
                Sentry.captureException(`[Socket] connect failed: ${typeof err === "string" ? err : JSON.stringify(err)}`)
                console.warn("connect failed", "reconnecting...")
            })

            connection.on("reconnect_error", (err: any) => {
                onError && onError(err)
                Sentry.captureException(`[Socket] connect failed: ${typeof err === "string" ? err : JSON.stringify(err)}`)
                console.warn("reconnect error", "retry in 1min")
            })

            connection.on("connect", () => {
                console.log("Socket Connected", connection.connected)
                console.log("waiting for user auth")
                self.socket = connection
                onSuccess && onSuccess()
            })

            // Connection succeeded
            connection.on("success", (data: { message: string; user: User }) => {
                console.log("Message: ", data.message)
                self.socket = connection
                onSuccess && onSuccess()
                self.subscribeToChange()
            })

            connection.on("error", async (err: { [key: string]: string }) => {
                onError && onError(err.message)
                if (LocalStorageService.isAuthorized() && err.message === "Token expired") {
                    if (!this.isReconnecting) {
                        this.isReconnecting = true
                        await self.reconnect()
                    }
                    return
                }

                connection.disconnect()
            })

            connection.on("disconnect", () => {
                if (self.socket?.nsp && !LocalStorageService.isAuthorized()) {
                    self.disconnect("LoggingOut")
                    return
                }

                onError && onError("disconnected")
            })
        } else if (this.socket.connected) {
            // this console.logs will appears only in dev mode by default (do not remove)
            console.log("Connecting socket")
            console.log("socket status", self.socket.connected)
            onSuccess && onSuccess()
        }

        return connection
    }

    // Check if socket reconnection is firing then delete this
    reconnect = async (refresh = true) => {
        if (LocalStorageService.isAuthorized()) {
            refresh && (await AuthService.silentRefresh())
            await delay(3000)
            this.socket?.connect()
            await delay(2000)
            if (!this.socket?.connected) {
                this.connect(this.loggedInUserId, this.onErrorInstance, this.onSuccessInstance)
            }

            await delay(2000)
            this.isReconnecting = false
        } else {
            this.disconnect("No Access Token")
        }
    }

    setLocation = (location: string) => {
        this.location = location
    }

    checkAndNotify = (createdBy: string | number | any[] | Date, documentId: string | number | any[] | Date, isNotification: boolean): boolean => {
        const notOwnAction = this.loggedInUserId !== createdBy
        const isAboutMe = this.loggedInUserId === documentId
        if (notOwnAction || isAboutMe) {
            isNotification && this.dispatch(fetchAllActivities())
        }
        return notOwnAction || isAboutMe
    }

    isMyFilterUpdate = (data: any) => this.loggedInUserId === data.fullDocument.metaData.updatedFields.user

    subscribeToChange() {
        // socket.io/aws idle timeout handler
        this.socket.on("ping", (data: any) => {
            console.debug(`[socket] heartbeat: ${JSON.stringify(data)}`)
            this.socket.emit("pong", { beat: 1 })
        })

        this.socket.on(CollectionNameEnum.EXPENSE, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToExpenses(this.dispatch, data)
            else subscribeToExpenseCallbacks(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.MATCHING_TRANSACTION, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToTransactions(this.dispatch, data)
            else subscribeToTransactionsCallbacks(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.EXPENSE_ACCOUNT, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToExpenseAccount(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.COST_CENTER_2, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToCostCenter2(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.USER, (data: DocumentChangeInterface) => {
            if (data.fullDocument.operationType === OperationTypeEnum.LOGOUT) {
                this.socket.emit("disconnect")
                return
            }

            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument)))
                subscribeToEmployee(this.dispatch, data, this.loggedInUserId)
        })

        this.socket.on(CollectionNameEnum.VENDOR, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToVendor(this.dispatch, data)
            else subscribeToVendorCallbacks(data)
        })

        this.socket.on(CollectionNameEnum.COST_CENTER, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToCostCenter(this.dispatch, data)
            else subscribeToCostCenterCallbacks(data)
        })

        this.socket.on(CollectionNameEnum.COMPANY_SETTINGS, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToCompanySettings(this.dispatch, data)
            else subscribeToCompanyCallbacks(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.BANK_CONNECTION, (data: DocumentChangeInterface) => {
            subscribeToBankConnection(this.dispatch, data, this.loggedInUserId)
        })

        this.socket.on(CollectionNameEnum.PAYMENT_SETTINGS, (data: DocumentChangeInterface) => {
            subscribeToPaymentSettings(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.CURRENCY_EXCHANGE, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToCurrencyExchange(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.CARD, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument)))
                subscribeToCreditCards(this.dispatch, data, this.loggedInUserId)
            else subscribeToCreditCardCallbacks(data)
        })

        this.socket.on(CollectionNameEnum.WALLET, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToWallet(this.dispatch)
        })

        this.socket.on(CollectionNameEnum.CORPORATE, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToCorporate(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.INBOX_INVOICE, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToInboxInvoices(this.dispatch, data)
            else subscribeToInboxInvoiceCallbacks(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.TEAM, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToTeam(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.TAX, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToTax(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.WORKFLOW, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToWorkflow(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.IMPORT_RESULT, (data: DocumentChangeInterface) => {
            if (this.location === "/reports") this.dispatch(updateAnalytics())
            subscribeToImportResult(this.dispatch, data, this.loggedInUserId)
        })

        this.socket.on(CollectionNameEnum.TRANSACTION_CATEGORY, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument)))
                subscribeToTransactionCategory(this.dispatch, data)
            else subscribeToTransactionCategoryCallbacks(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.EXPENSE_TAG, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) {
                subscribeToExpenseTag(this.dispatch, data)
            }
        })

        this.socket.on(CollectionNameEnum.CSV_TEMPLATE, (data: DocumentChangeInterface) => {
            if (this.checkAndNotify(data.fullDocument.createdBy, data.fullDocument.documentId, isNotification(data.fullDocument))) subscribeToCsvTemplate(this.dispatch, data)
        })

        this.socket.on(CollectionNameEnum.FILTER, (data: any) => {
            if (this.isMyFilterUpdate(data) && !isIncomingFilterEqualToLocal(data)) {
                subscribeToFilter(data)
            }
        })
    }

    // disconnect - used when unmounting
    public async disconnect(reason: any) {
        const disconnectReason = `Socket disconnect: ${reason}`
        console.warn(disconnectReason)

        this.socket?.removeAllListeners()
        this.socket?.disconnect()
        this.socket?.close()
    }
}

export default new SocketService()
